import type { SoftPaywallController, SoftPaywallEventBus } from "@product/rosetta-sdk";
import {
  AbandonedCartFlowController,
  AbandonedCartFlowCookieStateStorage,
  AbandonedCartFlowEventBus,
} from "@product/rosetta-sdk";
import { useAtomValue } from "jotai";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";

import { config } from "shared/data";

import { rosettaAtom } from "./atoms";
import { useSoftPaywallIntegration } from "./features/soft-paywall/hooks";
import { useRosettaConfig } from "./hooks/rosetta-config";

type ContextValue = {
  abandonedCartFlow?: {
    controller: AbandonedCartFlowController;
    eventBus: AbandonedCartFlowEventBus;
  };
  softPaywall?: {
    controller: SoftPaywallController;
    eventBus: SoftPaywallEventBus;
  };
};

const RosettaContext = createContext<ContextValue>({});

export type Props = {
  children?: ReactNode;
};

export const RosettaContextProvider: React.FunctionComponent<Props> = ({ children }) => {
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const [instances, setInstances] = useState<ContextValue>({});

  const { rosettaConfigState } = useRosettaConfig();

  const softPaywall = useSoftPaywallIntegration({
    rosettaConfigState,
  });
  useEffect(() => {
    setInstances(instances => ({
      ...instances,
      softPaywall: softPaywall ?? undefined,
    }));
  }, [softPaywall]);

  useEffect(() => {
    if (!rosettaConfigState.result) return;

    const eventBus = new AbandonedCartFlowEventBus();
    const storage = new AbandonedCartFlowCookieStateStorage(config.rosetta.cookieDomain);
    const controller = new AbandonedCartFlowController({
      settings: rosettaConfigState.result.abandonedCartFlowSettings,
      storage,
    });

    const { cleanup } = controller.setup(eventBus);

    setInstances({
      abandonedCartFlow: {
        controller,
        eventBus,
      },
    });

    return cleanup;
  }, [rosettaConfigState]);

  useEffect(() => {
    instances?.abandonedCartFlow?.eventBus.emit({
      data: {
        hasAccess:
          asyncRosettaState?.result?.instance?.rulesEngine.getFact("hasActiveScmpConversions") ??
          false,
      },
      name: "AccessStatusUpdated",
    });
  }, [asyncRosettaState?.result?.instance?.rulesEngine, instances]);

  return <RosettaContext.Provider value={instances}>{children}</RosettaContext.Provider>;
};

RosettaContextProvider.displayName = "RosettaContextProvider";

export function useRosettaContext() {
  return useContext(RosettaContext);
}
